import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormatTranslatePipe } from 'src/app/pipes/format-translate.pipe';
import { TranslateKeyPipe } from 'src/app/pipes/translate-key/translate-key.pipe';
import { LanguageKey } from 'src/main/translate/language-key/language-key';

export interface TreeNode<T> {
  data: T;
  children: TreeNode<T>[];
  expanded: boolean;
  selected: boolean;
  indeterminate: boolean;
  directSelected: boolean;
}

@Component({
  selector: 'vs-tree-view',
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.scss'],
  standalone: true,
  imports: [FormsModule, CommonModule, TranslateKeyPipe, FormatTranslatePipe],
})
export class TreeViewComponent<T = any> implements OnChanges {
  public languageKey = LanguageKey;
  @Input() data: T[] = [];
  @Input() idProperty: string = 'id'; // idRootPai
  @Input() parentIdProperty: string = 'parent_id'; // idRootFilho
  @Input() labelProperty: string = 'name';
  @Input() colorProperty?: string;
  @Input() title?: string;
  @Input() registerType: string;
  @Input() maxHeigth: string = 'auto';
  @Input() selectedIds: any[] = [];
  @Input() defaultExpanded: boolean = true;
  @Input() selectDescendantsOnSelect: boolean = false;
  @Output() selectionChange = new EventEmitter<any[]>();

  treeNodes: TreeNode<T>[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['selectedIds']) {
      this.buildTree();
    }
  }

  private buildTree(): void {
    if (!this.data || this.data.length === 0) {
      this.treeNodes = [];
      return;
    }

    const selectedIdsSet = new Set(this.selectedIds ?? []);
    const map = new Map<any, TreeNode<T>>();
    const roots: TreeNode<T>[] = [];

    this.data.forEach((item) => {
      const id = (item as any)[this.idProperty];
      const directSelected = selectedIdsSet.has(id);
      map.set(id, {
        data: item,
        children: [],
        expanded: this.defaultExpanded,
        selected: directSelected,
        indeterminate: false,
        directSelected,
      });
    });

    this.data.forEach((item) => {
      const id = (item as any)[this.idProperty];
      const parentId = (item as any)[this.parentIdProperty];
      const node = map.get(id)!;

      if (parentId && map.has(parentId)) {
        map.get(parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    this.treeNodes = roots;
    this.updateTreeStates();
  }

  onCheckboxChange(node: TreeNode<T>): void {
    const isSelected = node.selected;

    if (isSelected) {
      node.directSelected = true;
      if (this.selectDescendantsOnSelect) {
        this.selectBranch(node);
      }
    } else {
      this.clearBranchSelection(node);
    }

    this.updateTreeStates();
    this.emitSelection();
  }

  private clearBranchSelection(node: TreeNode<T>): void {
    node.directSelected = false;
    node.selected = false;
    node.indeterminate = false;

    node.children.forEach((child) => {
      this.clearBranchSelection(child);
    });
  }

  private selectBranch(node: TreeNode<T>): void {
    node.directSelected = true;
    node.selected = true;
    node.indeterminate = false;

    node.children.forEach((child) => {
      this.selectBranch(child);
    });
  }

  private updateTreeStates(): void {
    const syncNodeState = (node: TreeNode<T>): boolean => {
      const hasSelectedChildren = node.children.some((child) =>
        syncNodeState(child),
      );

      node.selected = node.directSelected || hasSelectedChildren;
      node.indeterminate = !node.directSelected && hasSelectedChildren;

      return node.selected;
    };

    this.treeNodes.forEach((node) => {
      syncNodeState(node);
    });
  }

  private emitSelection(): void {
    const selectedList: T[] = [];

    const collect = (nodes: TreeNode<T>[]) => {
      nodes.forEach((node) => {
        if (node.selected) {
          selectedList.push(node.data);
        }

        if (node.children && node.children.length > 0) {
          collect(node.children);
        }
      });
    };

    collect(this.treeNodes);

    this.selectionChange.emit(
      selectedList.map((item) => (item as any)[this.idProperty]),
    );
  }

  toggleNode(node: TreeNode<T>): void {
    node.expanded = !node.expanded;
  }

  getColor(node: TreeNode<T>): string | null {
    return this.colorProperty ? (node.data as any)[this.colorProperty] : null;
  }
}
